import type { MediaAsset, MediaBankActiveFilters, MediaBankFilterGroupItem, MediaDescriptionMetadata } from '#constants/types/mediaBank';
import type { ProductRaw, MediaSet } from '#constants/types/algolia';
import { NorceMediaSetCodes } from '#constants/norceCodes';
import useAlgoliaFetch from '~/composables/useAlgoliaFetch';
import { useGlobalContentStore } from '~/store/globalContent';
import { useUserStore } from '~/store/user';

/**
 * Parses the JSON metadata from a media description field.
 * Returns default values if parsing fails or description is empty.
 */
function parseMediaDescription(description: string | undefined): MediaDescriptionMetadata {
  const defaults: MediaDescriptionMetadata = {
    ResourceSubType: [],
    ResourceRoomType: [],
    OriginalTiffUrl: null,
    OriginalJpgUrl: null,
  };
  if (!description || !description.trim()) { return defaults; }
  try {
    const parsed = JSON.parse(description);
    return {
      ResourceSubType: Array.isArray(parsed.ResourceSubType) ? parsed.ResourceSubType : [],
      ResourceRoomType: Array.isArray(parsed.ResourceRoomType) ? parsed.ResourceRoomType : [],
      OriginalTiffUrl: typeof parsed.OriginalTiffUrl === 'string' ? parsed.OriginalTiffUrl : null,
      OriginalJpgUrl: typeof parsed.OriginalJpgUrl === 'string' ? parsed.OriginalJpgUrl : null,
    };
  } catch {
    return defaults;
  }
}

/**
 * Normalizes a label string: converts UPPERCASE or camelCase to "Title case"
 * e.g., "KITCHEN" -> "Kitchen", "SwatchImage" -> "Swatch image"
 */
function normalizeLabel(str: string): string {
  if (!str) { return ''; }
  // Insert space before uppercase letters (for camelCase/PascalCase)
  const spaced = str.replace(/([a-z])([A-Z])/g, '$1 $2');
  // Capitalize first letter, lowercase rest
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

// e.g. "SWATCH_CLOSEUP" -> "swatchCloseup", for building i18n key suffixes from data codes
function toCamelKey(code: string): string {
  return code.toLowerCase().replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
}

/**
 * Translates a data-driven code via an i18n key (e.g. `${keyPrefix}.${toCamelKey(code)}`).
 * Falls back to the normalized raw code when no translation exists for it yet -
 * these values come from Norce/PIM metadata and aren't all seeded in PoeEditor.
 */
function translateCode(t: (key: string) => string, keyPrefix: string, code: string): string {
  const key = `${keyPrefix}.${toCamelKey(code)}`;
  const translated = t(key);
  return translated === key ? normalizeLabel(code) : translated;
}

// Document codes for Media Bank
const DOCUMENT_CODES = new Set<string>([
  NorceMediaSetCodes.TDS,
  NorceMediaSetCodes.InstallationDocs,
  NorceMediaSetCodes.CleaningAndMaintenanceDocs,
  NorceMediaSetCodes.DeclarationDocs,
  NorceMediaSetCodes.WarrantyDocs,
  NorceMediaSetCodes.GaranLabels,
]);

// Allowed image codes for Media Bank - only these specific types are shown
const ALLOWED_IMAGE_CODES = new Set<string>([
  NorceMediaSetCodes.PrimaryImage,
  NorceMediaSetCodes.SwatchImage,
  NorceMediaSetCodes.SwatchCloseUp,
  NorceMediaSetCodes.CADImage,
  NorceMediaSetCodes.InteriorImage,
  NorceMediaSetCodes.InspirationImage,
  NorceMediaSetCodes.SchematicImages,
  NorceMediaSetCodes.AccessoryImage,
  NorceMediaSetCodes.Illustrations,
  NorceMediaSetCodes.ExplodedViews,
]);

// Allowed ResourceSubType values for Image Type filter
// Only these values will appear in the filter dropdown
const ALLOWED_RESOURCE_SUB_TYPES = new Set<string>([
  'ACCESSORIES',
  'ACCESSORY',
  'CAD',
  'ILLUSTRATION',
  'INSPIRATION',
  'INTERIOR',
  'SCHEMATIC',
  'SWATCH',
  'SWATCH_CLOSEUP',
]);

// Known top-level product types for filtering
const PRODUCT_TYPES = ['Wood', 'Vinyl', 'Resilient', 'Accessories'];

// Categories whose documents are excluded from the media bank (still visible on PDP)
const DOCUMENT_CATEGORY_BLACKLIST = new Set(['karelia']);

// Algolia attributes to retrieve for media bank queries — limits payload to only needed fields
const MEDIA_BANK_ATTRIBUTES = ['name', 'variantName', 'partNo', 'categoryFilters', 'parametrics', 'mediaSets'];

function classifyMediaType(code: string): 'Images' | 'Documents' | null {
  if (ALLOWED_IMAGE_CODES.has(code)) { return 'Images'; }
  if (DOCUMENT_CODES.has(code)) { return 'Documents'; }
  return null;
}

/**
 * Filter documents based on language preferences
 * - English locales: Show all documents
 * - Other locales: Show local docs, show English "(en)" only if no local version exists
 */
function filterDocumentsByLanguage(media: MediaSet['media'], locale: string): MediaSet['media'] {
  const withNames = media.filter((doc) => !!doc.name);

  if (locale.startsWith('en')) {
    return withNames;
  }

  const englishSuffix = '(en)';
  const localDocuments: MediaSet['media'] = [];
  const englishDocuments: (MediaSet['media'][0] & { baseName: string })[] = [];

  withNames.forEach((doc) => {
    if (doc.name.endsWith(englishSuffix)) {
      const baseName = doc.name.slice(0, -englishSuffix.length).trim();
      englishDocuments.push({ ...doc, baseName });
    } else {
      localDocuments.push(doc);
    }
  });

  const filteredEnglishDocs = englishDocuments.filter(
    (englishDoc) => !localDocuments.some((localDoc) => localDoc.name === englishDoc.baseName)
  );

  return [...localDocuments, ...filteredEnglishDocs];
}

function buildOptions(
  base: MediaAsset[],
  selected: string[],
  getKey: (a: MediaAsset) => string,
  getLabel: (a: MediaAsset) => string
): MediaBankFilterGroupItem['options'] {
  const map = new Map<string, { label: string; count: number }>();
  base.forEach((a) => {
    const key = getKey(a);
    if (!key) { return; }
    const existing = map.get(key);
    if (existing) {
      existing.count++;
    } else {
      map.set(key, { label: getLabel(a), count: 1 });
    }
  });
  return Array.from(map.entries())
    .map(([code, { label, count }]) => ({
      code,
      label,
      count,
      selected: selected.includes(code),
    }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Builds filter options from an array field on each asset.
 * Each value in the array is counted separately.
 * @param allowedValues - Optional set of allowed values. If provided, only these values will be included.
 */
function buildOptionsFromArray(
  base: MediaAsset[],
  selected: string[],
  getArray: (a: MediaAsset) => string[],
  t: (key: string) => string,
  keyPrefix: string,
  allowedValues?: Set<string>
): MediaBankFilterGroupItem['options'] {
  const map = new Map<string, number>();
  base.forEach((a) => {
    const arr = getArray(a);
    arr.forEach((val) => {
      if (!val) { return; }
      // Skip values not in allowed list (if provided)
      if (allowedValues && !allowedValues.has(val.toUpperCase())) { return; }
      map.set(val, (map.get(val) ?? 0) + 1);
    });
  });
  return Array.from(map.entries())
    .map(([code, count]) => ({
      code,
      label: translateCode(t, keyPrefix, code),
      count,
      selected: selected.includes(code),
    }))
    .sort((a, b) => b.count - a.count);
}

function flattenToAssets(hits: ProductRaw[], locale: string): MediaAsset[] {
  const assets: MediaAsset[] = [];
  // Track seen documents by name + type to avoid duplicates (same document shared across products)
  const seenDocuments = new Set<string>();

  hits.forEach((hit) => {
    const productName = hit.name ?? '';
    const variantName = hit.variantName ?? '';
    const articleNumber = hit.partNo ?? '';
    // Find product type from categoryFilters (flat array) by matching known types
    const productType = hit.categoryFilters?.find((c) => PRODUCT_TYPES.includes(c)) ?? '';
    const collection = (hit.parametrics?.find((p) => p.code === 'collection')?.value as string) ?? '';

    const mediaSets = hit.mediaSets ?? [];
    mediaSets.forEach((ms) => {
      const mediaType = classifyMediaType(ms.code);
      if (!mediaType) { return; }

      // For documents, filter by language preference
      const mediaItems = mediaType === 'Documents'
        ? filterDocumentsByLanguage(ms.media ?? [], locale)
        : ms.media ?? [];

      mediaItems.forEach((media) => {
        if (!media.url) { return; }

        // Skip documents from blacklisted categories (e.g. Karelia)
        if (mediaType === 'Documents' && hit.categoryFilters?.some((c) => DOCUMENT_CATEGORY_BLACKLIST.has(c.toLowerCase()))) {
          return;
        }

        // Skip duplicate documents (same name + type across different products)
        if (mediaType === 'Documents') {
          const docKey = `${media.name || ''}_${ms.code}`;
          if (seenDocuments.has(docKey)) { return; }
          seenDocuments.add(docKey);
        }

        // Parse JSON metadata from media item description (not mediaSet description)
        const metadata = parseMediaDescription(media.description);

        assets.push({
          id: `${hit.partNo}_${ms.code}_${media.id}`,
          productName,
          variantName,
          articleNumber,
          collection,
          productType,
          mediaType,
          mediaSetCode: ms.code,
          mediaSetTitle: ms.title || ms.code,
          url: media.url,
          thumbnailUrl: media.url,
          mimeType: media.mimeType || 'application/octet-stream',
          fileType: media.fileType || '',
          name: media.name || ms.title || ms.code,
          // Parsed from media item description JSON
          resourceSubTypes: metadata.ResourceSubType,
          resourceRoomTypes: metadata.ResourceRoomType,
          originalTiffUrl: metadata.OriginalTiffUrl,
          originalJpgUrl: metadata.OriginalJpgUrl,
        });
      });
    });
  });
  return assets;
}

export type MediaBankOptions = {
  documentsOnly?: boolean;
  excludeProductTypes?: string[];
};

export default function useMediaBank(options?: Ref<MediaBankOptions> | ComputedRef<MediaBankOptions>) {
  const globalContentStore = useGlobalContentStore();
  const userStore = useUserStore();
  const { freeTextSearch, getFromAlgolia } = useAlgoliaFetch();
  const { $t } = useNuxtApp();
  const locale = computed(() => globalContentStore.getLocale);

  // Reactive options with defaults
  const opts = computed(() => ({
    documentsOnly: options?.value?.documentsOnly ?? false,
    excludeProductTypes: options?.value?.excludeProductTypes ?? [],
  }));

  // Store loaded assets
  const allAssets = ref<MediaAsset[]>([]);
  const loading = ref(true);
  const searchQuery = ref('');
  const searchTimer = ref<ReturnType<typeof setTimeout> | null>(null);
  const minSearchLength = 2;
  const hasSearched = ref(false);
  const activeFilters = ref<MediaBankActiveFilters>({
    productType: [],
    mediaType: [],
    imageType: [],
    documentType: [],
    roomType: [],
  });

  // Tracks the most recent fetch so late-arriving pages from a superseded
  // search/load can't clobber newer results (no requests are actually aborted)
  let currentRequestId = 0;

  const fetchPage = (query: string, page: number) =>
    freeTextSearch<ProductRaw[]>(query, 1000, '_search', false, false, false, ['categoryFilters:Wood'], page, MEDIA_BANK_ATTRIBUTES);

  // Streams results page by page: the first page renders immediately, remaining
  // pages are fetched in the background and merged in as they arrive
  const fetchAllHits = async(query: string, requestId: number, onPage: (hits: ProductRaw[]) => void): Promise<void> => {
    const res0 = await fetchPage(query, 0);
    if (requestId !== currentRequestId) { return; }
    onPage(res0?.hits ?? []);

    const totalPages = Math.ceil((res0?.nbHits ?? 0) / 1000);
    if (totalPages > 1) {
      await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          fetchPage(query, i + 1).then((res) => {
            if (requestId !== currentRequestId) { return; }
            onPage(res?.hits ?? []);
          })
        )
      );
    }
  };

  const runFetch = async(query: string) => {
    const requestId = ++currentRequestId;
    loading.value = true;
    let hits: ProductRaw[] = [];
    let receivedFirstPage = false;
    await fetchAllHits(query, requestId, (pageHits) => {
      hits = hits.concat(pageHits);
      allAssets.value = hits.length ? flattenToAssets(hits, locale.value) : [];
      receivedFirstPage = true;
      loading.value = false;
    });
    if (requestId === currentRequestId && !receivedFirstPage) {
      loading.value = false;
    }
  };

  // Debounced search - calls Algolia when user types
  const doSearch = async(query: string) => {
    if (query.length < minSearchLength) {
      allAssets.value = [];
      return;
    }
    await runFetch(query);
  };

  // Watch searchQuery and debounce the search
  watch(searchQuery, (newQuery) => {
    if (searchTimer.value) {
      clearTimeout(searchTimer.value);
    }
    if (newQuery.length < minSearchLength) {
      // Reload default assets when search is cleared
      if (hasSearched.value) {
        loadAssets();
      }
      return;
    }
    hasSearched.value = true;
    searchTimer.value = setTimeout(() => {
      doSearch(newQuery);
    }, 500);
  });

  // Clean up timer on unmount
  onUnmounted(() => {
    if (searchTimer.value) {
      clearTimeout(searchTimer.value);
    }
  });

  // Load default/popular products when no search query
  const loadAssets = async() => {
    await runFetch('*');
  };

  // Shared filter function - excludeKey is optional, used for faceted filter counts
  const applyFilters = (excludeKey?: keyof MediaBankActiveFilters): MediaAsset[] => {
    let result = allAssets.value;

    // Mode-based filters (always applied)
    if (opts.value.documentsOnly) {
      result = result.filter((a) => a.mediaType === 'Documents');
    }
    if (opts.value.excludeProductTypes.length) {
      result = result.filter((a) => !opts.value.excludeProductTypes.includes(a.productType));
    }

    // User-selected filters (skip the excluded key for faceted counts)
    if (excludeKey !== 'productType' && activeFilters.value.productType.length) {
      result = result.filter((a) => activeFilters.value.productType.includes(a.productType));
    }
    if (excludeKey !== 'mediaType' && activeFilters.value.mediaType.length) {
      result = result.filter((a) => activeFilters.value.mediaType.includes(a.mediaType));
    }
    if (excludeKey !== 'imageType' && activeFilters.value.imageType.length) {
      result = result.filter((a) => a.resourceSubTypes.some((t) => activeFilters.value.imageType.includes(t)));
    }
    if (excludeKey !== 'documentType' && activeFilters.value.documentType.length) {
      result = result.filter((a) => activeFilters.value.documentType.includes(a.mediaSetCode));
    }
    if (excludeKey !== 'roomType' && activeFilters.value.roomType.length) {
      result = result.filter((a) => a.resourceRoomTypes.some((t) => activeFilters.value.roomType.includes(t)));
    }

    return result;
  };

  const filteredAssets = computed<MediaAsset[]>(() => {
    const result = applyFilters();
    // Group: images first, then documents
    const images = result.filter((a) => a.mediaType === 'Images');
    const documents = result.filter((a) => a.mediaType === 'Documents');
    return [...images, ...documents];
  });

  const filterGroups = computed<MediaBankFilterGroupItem[]>(() => {
    const showImages = !opts.value.documentsOnly && (
      !activeFilters.value.mediaType.length || activeFilters.value.mediaType.includes('Images')
    );
    const showDocuments =
      !activeFilters.value.mediaType.length || activeFilters.value.mediaType.includes('Documents');

    const groups: MediaBankFilterGroupItem[] = [];

    // Product type filter - counts based on all OTHER filters
    const productTypeBase = applyFilters('productType');
    const productTypeOptions = buildOptions(
      productTypeBase,
      activeFilters.value.productType,
      (a) => a.productType,
      (a) => globalContentStore.getFilterOption('categoryFilters.' + a.productType).name
    );
    if (productTypeOptions.length) {
      groups.push({
        key: 'productType',
        labelKey: 'mediaBank.filter.productType',
        options: productTypeOptions,
      });
    }

    // Media type filter (skip in B2C/documentsOnly mode)
    if (!opts.value.documentsOnly) {
      const mediaTypeBase = applyFilters('mediaType');
      groups.push({
        key: 'mediaType',
        labelKey: 'mediaBank.filter.mediaType',
        options: [
          {
            code: 'Images',
            label: 'mediaBank.filter.mediaType.images',
            count: mediaTypeBase.filter((a) => a.mediaType === 'Images').length,
            selected: activeFilters.value.mediaType.includes('Images'),
          },
          {
            code: 'Documents',
            label: 'mediaBank.filter.mediaType.documents',
            count: mediaTypeBase.filter((a) => a.mediaType === 'Documents').length,
            selected: activeFilters.value.mediaType.includes('Documents'),
          },
        ],
      });
    }

    if (showImages) {
      const imageTypeBase = applyFilters('imageType').filter((a) => a.mediaType === 'Images');
      if (imageTypeBase.length) {
        const imageTypeOptions = buildOptionsFromArray(
          imageTypeBase,
          activeFilters.value.imageType,
          (a) => a.resourceSubTypes,
          $t,
          'mediaBank.filter.imageType',
          ALLOWED_RESOURCE_SUB_TYPES
        );

        if (imageTypeOptions.length) {
          groups.push({
            key: 'imageType',
            labelKey: 'mediaBank.filter.imageType',
            options: imageTypeOptions,
          });
        }

        // Room type filter
        const roomTypeBase = applyFilters('roomType').filter((a) => a.mediaType === 'Images');
        const roomTypeOptions = buildOptionsFromArray(
          roomTypeBase,
          activeFilters.value.roomType,
          (a) => a.resourceRoomTypes,
          $t,
          'mediaBank.filter.roomType'
        );
        if (roomTypeOptions.length) {
          groups.push({
            key: 'roomType',
            labelKey: 'mediaBank.filter.roomType',
            options: roomTypeOptions,
          });
        }
      }
    }

    if (showDocuments) {
      const docTypeBase = applyFilters('documentType').filter((a) => a.mediaType === 'Documents');
      if (docTypeBase.length) {
        groups.push({
          key: 'documentType',
          labelKey: 'mediaBank.filter.documentType',
          options: buildOptions(
            docTypeBase,
            activeFilters.value.documentType,
            (a) => a.mediaSetCode,
            (a) => a.mediaSetTitle
          ),
        });
      }
    }

    return groups;
  });

  const setFilter = (key: keyof MediaBankActiveFilters, values: string[]) => {
    activeFilters.value[key] = values;

    // Clear related filters when mediaType changes
    if (key === 'mediaType') {
      const hasImages = values.length === 0 || values.includes('Images');
      const hasDocuments = values.length === 0 || values.includes('Documents');

      if (!hasImages) {
        activeFilters.value.imageType = [];
        activeFilters.value.roomType = [];
      }
      if (!hasDocuments) {
        activeFilters.value.documentType = [];
      }
    }
  };

  const resetFilters = () => {
    activeFilters.value = { productType: [], mediaType: [], imageType: [], documentType: [], roomType: [] };
  };

  const hasActiveFilters = computed(() =>
    Object.values(activeFilters.value).some((v) => v.length > 0)
  );

  // Fetches all media assets scoped to the user's fixed-price (nettopris) assortment.
  // Uses fixedPricelistIds as an additional Algolia filter on top of the standard pricelist filter.
  const fetchFixedPricelistAssets = async(): Promise<MediaAsset[]> => {
    const fixedPricelists = userStore.visibleFixedPricelistIds;
    if (!fixedPricelists.length) { return []; }

    const fixedFilter = `(${fixedPricelists.map((id) => `availableOnPricelists:${id}`).join(' OR ')})`;

    const fetchOnePage = (page: number) => getFromAlgolia<ProductRaw[]>(
      {
        query: '*',
        hitsPerPage: 1000,
        page,
        filters: fixedFilter,
        facetFilters: [['categoryFilters:-Samples']],
        optionalFilters: ['categoryFilters:Wood'],
        attributesToRetrieve: MEDIA_BANK_ATTRIBUTES,
      },
      '',
      '_search'
    );

    const assets: MediaAsset[] = [];
    const res0 = await fetchOnePage(0);
    if (!res0) { return assets; }
    assets.push(...flattenToAssets(res0.hits, locale.value));

    const totalPages = Math.ceil(res0.nbHits / 1000);
    if (totalPages > 1) {
      await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          fetchOnePage(i + 1).then((res) => {
            if (res) { assets.push(...flattenToAssets(res.hits, locale.value)); }
          })
        )
      );
    }

    return assets;
  };

  return {
    allAssets,
    filteredAssets,
    filterGroups,
    loading,
    searchQuery,
    activeFilters,
    hasActiveFilters,
    loadAssets,
    setFilter,
    resetFilters,
    fetchFixedPricelistAssets,
  };
}
